// Paying an order by card through Mollie (sculpt-api PLAN-payments.md M3): the button that sends an admin to
// Mollie's page, the saved card, and the return page Mollie redirects to (`/billing?order={id}`), which polls the
// order until Mollie's answer is in. The API reads the payment back from Mollie; the portal only asks.
// The saved card (M4): renewals are charged on it; an admin replaces it with a zero verification, in the organization's currency, on Mollie's page
// (back on `/billing?card=1`, which polls the card) or removes it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PROFILE_ANCHOR } from './billingProfile';
import { money } from './format';
import { Alert, Button, ButtonLink } from './ui';
import { ApiError, api } from '../services/api';
import type { BillingState, CardOut, Order, PaymentStatus, Subscription } from '../types';

export const POLL_EVERY_MS = 2000;
export const POLL_FOR_MS = 60000;
const CARD_ERRORS = ['payments_disabled', 'payment_provider_unavailable'];
const PAY_ERRORS = ['payments_disabled', 'order_not_payable', 'vat_review_pending', 'billing_profile_required', 'nothing_to_pay', 'payment_provider_unavailable'];
const ENDED: PaymentStatus[] = ['failed', 'canceled', 'expired'];

/** Whether an admin can pay this order by card: cards on for the organization, the order pending, its VAT not held
 *  for review, and something to pay. An order whose VAT waits for the billing profile is payable: the checkout asks
 *  for the profile first. */
export function payableByCard(order: Order, billing: BillingState, admin: boolean): boolean {
  return (
    admin &&
    billing.payments_enabled &&
    order.status === 'pending' &&
    order.vat_scheme !== 'review' &&
    Number(order.total ?? order.amount) > 0
  );
}

/** Refusal of a checkout, as a sentence (and the profile link when it is missing). */
function PayError({ code }: { code: string }) {
  const { t } = useTranslation();
  if (code === 'billing_profile_required') {
    return (
      <span>
        {t('billing.card.error.billing_profile_required')}{' '}
        <Link to={`/organization#${PROFILE_ANCHOR}`} className="text-accent underline">
          {t('billing.vat.noProfileLink')}
        </Link>
      </span>
    );
  }
  return <span>{t(PAY_ERRORS.includes(code) ? `billing.card.error.${code}` : 'app.error')}</span>;
}

/** Start a checkout and leave for Mollie's page. */
async function startCheckout(orderId: number): Promise<void> {
  const out = await api.checkout(orderId);
  if (!out.checkout_url) throw new ApiError(502, 'payment_provider_unavailable', '');
  window.location.assign(out.checkout_url);
}

/** « Payer 120,00 € par carte »: opens the checkout's payment step for this order (`/checkout?order={id}`), where
 *  the admin accepts the terms of sale, and completes the billing profile first when the order's VAT waits for it. */
export function PayLink({ order, compact = false }: { order: Order; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <ButtonLink to={`/checkout?order=${order.id}`} className={compact ? 'py-1! text-[12px]' : ''} data-testid={`pay-${order.id}`}>
      {order.total !== null ? t('billing.card.pay', { total: money(order.total, order.currency, i18n.language) }) : t('checkout.payOrder')}
    </ButtonLink>
  );
}

/** "09/2028" from "2028-09-30". */
function expiry(value: string): string {
  const [year, month] = value.split('-');
  return `${month}/${year}`;
}

/** « Carte enregistrée : Visa •••• 4242, expire 09/2028 ». */
export function savedCardText(billing: BillingState, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  if (!billing.card_label) return null;
  return billing.card_expires_on ? t('billing.card.savedValue', { label: billing.card_label, expiry: expiry(billing.card_expires_on) }) : billing.card_label;
}

type Outcome = { kind: 'checking' } | { kind: 'paid' } | { kind: 'ended'; status: PaymentStatus } | { kind: 'waiting' } | { kind: 'error' };

/** Back from Mollie: poll the order every POLL_EVERY_MS for POLL_FOR_MS until it is paid or its payment ended.
 *  `onSettled` runs once the outcome is known (the page reloads its data and drops `?order=` from the URL). */
export function PaymentReturn({ orderId, onSettled }: { orderId: number; onSettled: (paid: boolean) => void }) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'checking' });
  const [retry, setRetry] = useState<string | null>(null);
  const settled = useRef(onSettled);
  settled.current = onSettled;

  const poll = useCallback(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + POLL_FOR_MS;
    const tick = async () => {
      let order: Order | null = null;
      try {
        order = await api.order(orderId);
      } catch {
        order = null;
      }
      if (cancelled) return;
      if (order?.status === 'paid') {
        setOutcome({ kind: 'paid' });
        settled.current(true);
      } else if (order?.payment_status && ENDED.includes(order.payment_status)) {
        setOutcome({ kind: 'ended', status: order.payment_status });
        settled.current(false);
      } else if (order && order.status !== 'pending') {
        setOutcome({ kind: 'error' });
        settled.current(false);
      } else if (Date.now() + POLL_EVERY_MS > deadline) {
        setOutcome({ kind: 'waiting' });
        settled.current(false);
      } else {
        timer = setTimeout(() => void tick(), POLL_EVERY_MS);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId]);

  useEffect(() => poll(), [poll]);

  const again = async () => {
    setRetry(null);
    try {
      await startCheckout(orderId);
    } catch (err) {
      setRetry(err instanceof ApiError ? err.code : 'error');
    }
  };

  return (
    <div data-testid="payment-return">
      {outcome.kind === 'checking' && <Alert kind="info">{t('billing.card.return.checking')}</Alert>}
      {outcome.kind === 'paid' && <Alert kind="success">{t('billing.card.return.paid')}</Alert>}
      {outcome.kind === 'waiting' && <Alert kind="info">{t('billing.card.return.waiting')}</Alert>}
      {outcome.kind === 'error' && <Alert kind="error">{t('billing.card.error.order_not_payable')}</Alert>}
      {outcome.kind === 'ended' && (
        <Alert kind="error">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{t(`billing.card.return.${outcome.status}`)}</span>
            <Button variant="secondary" className="py-1! text-[12px]" onClick={() => void again()}>
              {t('billing.card.retry')}
            </Button>
          </div>
          {retry && (
            <div className="mt-2">
              <PayError code={retry} />
            </div>
          )}
        </Alert>
      )}
    </div>
  );
}


/** Leave for Mollie's page to save a card (a zero verification in the organization's currency). */
async function startCardUpdate(): Promise<void> {
  const out = await api.updateCard();
  if (!out.checkout_url) throw new ApiError(502, 'payment_provider_unavailable', '');
  window.location.assign(out.checkout_url);
}

function cardError(code: string, t: (k: string) => string): string {
  return t(CARD_ERRORS.includes(code) ? `billing.card.error.${code}` : 'app.error');
}

/** Whether the saved card expires before the subscription's current period ends (its next renewal). */
export function expiresBeforeRenewal(billing: BillingState, sub: Subscription | null): boolean {
  return !!billing.card_expires_on && !!sub && billing.card_expires_on < sub.current_period_end.slice(0, 10);
}

/** « Carte enregistrée »: the card renewals are charged on, and how to replace or remove it (admins). */
export function SavedCardCard({ billing, sub, onChanged }: { billing: BillingState; sub: Subscription | null; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const card = savedCardText(billing, t);
  const change = async () => {
    setBusy(true);
    setError(null);
    try {
      await startCardUpdate();
    } catch (err) {
      setError(cardError(err instanceof ApiError ? err.code : 'error', t));
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(t('billing.card.removeConfirm'))) return;
    setBusy(true);
    setError(null);
    try {
      await api.removeCard();
      await onChanged();
    } catch (err) {
      setError(cardError(err instanceof ApiError ? err.code : 'error', t));
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line-soft bg-pane p-4 text-[13px]" data-testid="saved-card">
      <strong className="font-medium text-ink">{t('billing.card.saved')}</strong>
      {card ? (
        <>
          <span>{card}</span>
          <span className="text-muted">{t('billing.card.renewalsAuto')}</span>
          {expiresBeforeRenewal(billing, sub) && <Alert kind="warn">{t('billing.card.expiresBeforeRenewal')}</Alert>}
        </>
      ) : (
        <span className="text-muted">{t('billing.card.none')}</span>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" onClick={() => void change()} disabled={busy} data-testid="change-card">
          {busy ? t('billing.card.redirecting') : card ? t('billing.card.change') : t('billing.card.register')}
        </Button>
        {card && (
          <Button variant="ghost" className="text-[12px] underline" onClick={() => void remove()} disabled={busy} data-testid="remove-card">
            {t('billing.card.remove')}
          </Button>
        )}
      </div>
      <span className="text-[12px] text-faint">{t('billing.card.verificationHint')}</span>
      {error && <Alert kind="error">{error}</Alert>}
    </div>
  );
}

type CardOutcome = { kind: 'checking' } | { kind: 'paid' } | { kind: 'ended'; status: PaymentStatus } | { kind: 'waiting' };

/** Back from Mollie after saving a card: poll the card every POLL_EVERY_MS for POLL_FOR_MS until the update is in. */
export function CardReturn({ onSettled }: { onSettled: (saved: boolean) => void }) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<CardOutcome>({ kind: 'checking' });
  const [retry, setRetry] = useState<string | null>(null);
  const settled = useRef(onSettled);
  settled.current = onSettled;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + POLL_FOR_MS;
    const tick = async () => {
      let card: CardOut | null = null;
      try {
        card = await api.card();
      } catch {
        card = null;
      }
      if (cancelled) return;
      if (card?.update_status === 'paid') {
        setOutcome({ kind: 'paid' });
        settled.current(true);
      } else if (card?.update_status && ENDED.includes(card.update_status)) {
        setOutcome({ kind: 'ended', status: card.update_status });
        settled.current(false);
      } else if (Date.now() + POLL_EVERY_MS > deadline) {
        setOutcome({ kind: 'waiting' });
        settled.current(false);
      } else {
        timer = setTimeout(() => void tick(), POLL_EVERY_MS);
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const again = async () => {
    setRetry(null);
    try {
      await startCardUpdate();
    } catch (err) {
      setRetry(cardError(err instanceof ApiError ? err.code : 'error', t));
    }
  };

  return (
    <div data-testid="card-return">
      {outcome.kind === 'checking' && <Alert kind="info">{t('billing.card.update.checking')}</Alert>}
      {outcome.kind === 'paid' && <Alert kind="success">{t('billing.card.update.paid')}</Alert>}
      {outcome.kind === 'waiting' && <Alert kind="info">{t('billing.card.return.waiting')}</Alert>}
      {outcome.kind === 'ended' && (
        <Alert kind="error">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{t(`billing.card.update.${outcome.status}`)}</span>
            <Button variant="secondary" className="py-1! text-[12px]" onClick={() => void again()}>
              {t('billing.card.retry')}
            </Button>
          </div>
          {retry && <div className="mt-2">{retry}</div>}
        </Alert>
      )}
    </div>
  );
}
